import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Gift } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/invite")({
  head: () => pageHead({ path: "/invite", title: "Invite friends — Book Swap", description: "Invite friends and neighbours to Book Swap so there are more books to borrow nearby.", noindex: true }),
  component: Invite,
});

function randomCode() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

function Invite() {
  const { user } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data } = await supabase.from("referral_codes").select("code").eq("user_id", user.id).maybeSingle();
      if (data) setCode(data.code);
      else {
        const newCode = randomCode();
        const { error } = await supabase.from("referral_codes").insert({ user_id: user.id, code: newCode });
        if (!error) setCode(newCode);
      }
      const { count: c } = await supabase.from("referrals").select("*", { count: "exact", head: true }).eq("inviter_id", user.id);
      setCount(c ?? 0);
      setLoading(false);
    })();
  }, [user]);

  if (!user) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center">Sign in to get your invite link.</p></div>;

  const url = code ? `${window.location.origin}/register?ref=${code}` : "";

  async function share() {
    if (navigator.share) await navigator.share({ title: "Join me on Book Swap", text: "Trade books with neighbors. Free.", url });
    else { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="flex items-center gap-2 text-3xl font-bold"><Gift className="h-7 w-7 text-primary" /> Invite a reader</h1>
        <p className="mt-2 text-muted-foreground">Share Book Swap with friends. Every reader you bring earns you a Community Builder badge.</p>

        <div className="mt-8 rounded-3xl bg-card p-6 shadow-[var(--shadow-card)]">
          {loading ? <p className="text-muted-foreground">Generating your code…</p> : (
            <>
              <p className="text-sm font-semibold text-muted-foreground">Your invite link</p>
              <div className="mt-2 flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-sm">
                <code className="flex-1 truncate">{url}</code>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied!"); }}><Copy className="h-4 w-4" /></Button>
              </div>
              <Button onClick={share} className="mt-4 w-full bg-primary hover:bg-primary/90"><Share2 className="mr-2 h-4 w-4" /> Share invite</Button>
              <p className="mt-4 text-center text-sm text-muted-foreground">{count} {count === 1 ? "neighbor has" : "neighbors have"} joined through you 🌱</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
